import _, { sumBy, flatten, groupBy } from 'lodash'
import { BindAll } from 'lodash-decorators'

import { computed, observable, action } from 'mobx'

import { formatOrderMaterialUnitCost, resetCompanyMaterialId, makeOrderMaterial } from 'common/helpers/order'
import { calcExtCost } from 'common/helpers/order'
import { OrderMaterial } from 'common/server/orders'

import { show, update, Order, comment } from 'vendor/server/public/orders'

@BindAll()
export default class OrderStore {
  // Order Materials to add to a new order
  @observable selectedOrder: Nullable<Order> = null
  @observable orderMaterials: { [deliveryId: string]: OrderMaterial[] } = {}

  // Doesn't cover scenario when you make a change then change back
  @observable selectedOrderDirty = false
  @observable newComment = ''

  getOrderMaterialsByDeliveryId(deliveryId: string) {
    return this.orderMaterials[deliveryId]
  }

  @action
  updateOrderMaterialsByDeliveryId(deliveryId: string, newOrderMaterials = []) {
    this.orderMaterials[deliveryId] = newOrderMaterials
  }

  getPlainOrderMaterials() {
    const orderMaterialsMatrix = Object.keys(this.orderMaterials).map((key) => {
      return this.orderMaterials[key]
    }, [])

    return flatten(orderMaterialsMatrix)
  }

  // EXISTING ORDERS
  @action
  async selectOrder(id: string | null) {
    this.selectedOrderDirty = false
    this.newComment = ''

    if (!id) {
      this.selectedOrder = null
      this.orderMaterials = {}
      return
    }

    this.selectedOrder = (await show(id)).data
    this.orderMaterials = groupBy(this.selectedOrder.order_materials, 'delivery_id')
  }

  async updateOrder(
    company_vendor_contact_id: string,
    state_changes: {
      confirmed_at?: string
      rejected_at?: string
      cancellation_rejected_at?: string
      cancellation_accepted_at?: string
    },
    vendor_files_signed_ids: string[],
    vendor_files_delete_ids: string[],
    quote_signed_id: string,
  ): Promise<void> {
    const { id, deliveries, state, quote_scheduled_to_expire_at } = this.selectedOrder
    const orderMaterials = this.getPlainOrderMaterials()

    const payload = {
      id,
      updated_at: this.selectedOrder?.updated_at,
      comment: this.newComment,
      company_vendor_contact_id,
      state_changes,
      deliveries,
      order_materials: orderMaterials
        .map(resetCompanyMaterialId)
        .map(makeOrderMaterial)
        .map(formatOrderMaterialUnitCost),
      quote_signed_id,
      state,
      vendor_files_delete_ids,
      vendor_files_signed_ids,
      quote_scheduled_to_expire_at,
    }
    await update(payload)
    this.selectOrder(id)
  }

  @action
  updateSelectedOrder(path: string, value: string | number): void {
    _.set(this.selectedOrder, path, value)
    this.selectedOrderDirty = true
  }

  @action
  updateNewComment(newComment: string) {
    this.newComment = newComment
    this.selectedOrderDirty = true
  }

  @computed get orderMaterialSummary(): OrderMaterial[] {
    // HACK: Grouping by description even though you could have the same description and different product ids
    // Imagine that will be rare in an order though so for now it should do
    const orderMaterials = this.getPlainOrderMaterials()
    const quantized = orderMaterials.map((om) => ({
      ...om,
      quantity: om.quantity ? parseInt(om.quantity as unknown as string) : null,
    }))
    const grouped = _.groupBy(quantized, 'company_material.description')
    const data = _.values(grouped).map((group) => {
      const sum = _.sumBy(group, 'quantity')
      return { ...group[0], quantity: sum }
    })
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return _.filter(data, (om) => om.company_material.description) as any
  }

  materialsCount(deliveryId): number {
    return (this.getOrderMaterialsByDeliveryId(deliveryId) || []).length
  }

  deliveryTotalCost(delivery_id): number {
    const deliveryMaterials = this.getOrderMaterialsByDeliveryId(delivery_id) || []

    const subTotal = sumBy(deliveryMaterials, (orderMaterial) => {
      const unit = orderMaterial?.company_material?.unit

      return calcExtCost({
        unitCost: orderMaterial?.unit_cost,
        quantity: orderMaterial?.quantity,
        multiplier: unit?.multiplier,
        qtyIncrement: unit?.qty_increment,
      })
    })

    const delivery = this.selectedOrder?.deliveries.find((delivery) => delivery.id === delivery_id)

    const taxValue = Number(delivery?.tax_value || 0)
    const shippingValue = Number(delivery?.shipping_value || 0)
    const otherValue = Number(delivery?.other_value || 0)

    const discountValue = Number(delivery?.discount_value || 0)

    const extraValue = taxValue + shippingValue + otherValue

    return subTotal + extraValue - discountValue
  }

  @action
  setOrderDirty(isDirty = true) {
    this.selectedOrderDirty = isDirty
  }

  async sendComment({ message, companyVendorContactId }) {
    await comment(this.selectedOrder.id, { comment: message, company_vendor_contact_id: companyVendorContactId })
    this.newComment = ''
    this.selectOrder(this.selectedOrder.id)
  }
}
